import Image from "next/image";
import Link from "next/link";
import { AER_STATS, AER_STORY, AER_STRIP, SECTION_IDS } from "./content";
import { Reveal } from "./reveal";
import { cn } from "@/app/utils/className";

const CaseLink = ({ href, label }: { href: string; label: string }) => (
  <Link
    href={href}
    className="dc-mono mt-7 inline-flex items-center gap-2.5 text-[12px] uppercase tracking-[.18em] text-mint transition-colors hover:text-paper"
  >
    {label} <span>→</span>
  </Link>
);

export const TechnologyInAction = () => (
  <section
    id={SECTION_IDS.work}
    className="relative border-t border-white/[.07] bg-ink-2 px-[clamp(16px,4vw,64px)] py-[clamp(96px,13vw,200px)]"
  >
    <div className="dc-shell">
      <div className="dc-mono mb-8 flex flex-wrap justify-end gap-x-7 gap-y-4 text-[12px] uppercase tracking-[.24em]">
        <span className="text-mist-4">Data layer 08 · Observation 1987–2026</span>
      </div>

      <h2 className="mt-0 mb-[clamp(40px,5vw,72px)] text-[clamp(34px,5.2vw,86px)] font-bold leading-[.96] tracking-[-.042em]">
        Our technology in action
      </h2>

      {/* Australia's Environment Report */}
      <div className="mb-[clamp(40px,5vw,64px)] grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] items-end gap-[clamp(28px,4vw,72px)]">
        <h3 className="m-0 text-[clamp(26px,3.4vw,52px)] font-semibold leading-[1.02] tracking-[-.032em]">
          Australia&apos;s Environment Report
        </h3>
        <p className="m-0 max-w-[50ch] text-[clamp(16px,1.4vw,20px)] leading-[1.65] text-mist-2">
          A comprehensive annual summary of the state of Australia&apos;s land,
          natural resources and ecosystems — built with TERN and the Australian
          National University.
        </p>
      </div>

      <div className="mb-0.5 grid grid-cols-1 gap-0.5 sm:grid-cols-2 lg:grid-cols-4">
        {AER_STRIP.map((frame) => (
          <div
            key={frame.img}
            className="relative h-[clamp(200px,22vw,340px)] min-w-0 overflow-hidden"
          >
            <Image
              src={frame.img}
              alt={frame.alt}
              fill
              sizes="(max-width: 768px) 100vw, 25vw"
              className="object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,15,28,.1),rgba(5,15,28,.7))]" />
          </div>
        ))}
      </div>

      {/* Number and unit are set apart so a figure like "10 million" never
          wraps and every label lines up across the band. */}
      <div className="mt-[clamp(56px,8vw,120px)] grid border-y border-white/[.14] md:grid-cols-3">
        {AER_STATS.map((stat, index) => (
          <div
            key={stat.kicker}
            className={cn(
              "py-[clamp(32px,4vw,64px)] md:px-[clamp(20px,2.6vw,48px)]",
              index === 0
                ? "md:pl-0"
                : "border-t border-white/[.14] md:border-t-0 md:border-l"
            )}
          >
            <div className="dc-mono mb-[clamp(20px,2.4vw,36px)] flex items-center gap-3 text-[12px] uppercase tracking-[.2em] text-mist-3">
              <span className="text-mint">0{index + 1}</span>
              <span className="h-px w-6 bg-white/25" />
              {stat.kicker}
            </div>
            <div className="flex flex-wrap items-baseline gap-x-3">
              <span className="whitespace-nowrap text-[clamp(56px,6.2vw,108px)] font-bold leading-[.9] tracking-[-.05em] text-paper">
                {stat.value}
              </span>
              <span className="text-[clamp(20px,2vw,32px)] font-semibold tracking-[-.02em] text-mint">
                {stat.unit}
              </span>
            </div>
            <p className="mt-5 mb-0 max-w-[28ch] text-[clamp(15px,1.2vw,18px)] leading-[1.55] text-mist-2">
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-[clamp(40px,5vw,68px)] grid grid-cols-[repeat(auto-fit,minmax(min(100%,250px),1fr))] gap-[clamp(24px,3vw,48px)]">
        {AER_STORY.map((entry, index) => (
          <Reveal key={entry.step} delay={index * 70}>
            <div className="dc-mono mb-3.5 text-[12px] uppercase tracking-[.2em] text-mint">
              {entry.step}
            </div>
            <p className="m-0 text-[17px] leading-[1.7] text-mist-2">
              {entry.body}
            </p>
          </Reveal>
        ))}
      </div>

      <CaseLink
        href="/case-studies/australia-environment-report"
        label="Read the full case study"
      />

      {/* VMAP */}
      <div className="mt-[clamp(80px,10vw,150px)] flex flex-wrap gap-[clamp(28px,4vw,64px)] border-t border-white/[.14] pt-[clamp(48px,6vw,88px)]">
        <div className="group relative min-h-[clamp(260px,30vw,420px)] min-w-0 flex-[1_1_340px] overflow-hidden">
          <Image
            src="/images/globseas2.webp"
            alt="Vegetation and forest change mapped at scale"
            fill
            sizes="(max-width: 900px) 100vw, 40vw"
            className="object-cover saturate-[.85] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,15,28,.15),rgba(5,15,28,.75))]" />
          <div className="dc-mono absolute bottom-5 left-[22px] text-[12px] uppercase tracking-[.2em] text-mint">
            Vegetation and forest change, mapped at scale
          </div>
        </div>

        <div className="min-w-0 flex-[1_1_440px]">
          <h3 className="mt-0 mb-5 text-[clamp(26px,3.4vw,52px)] font-semibold leading-[1.02] tracking-[-.032em] text-balance">
            Vegetation Monitoring &amp; Assessment Program (VMAP)
          </h3>
          <p className="mt-0 mb-[22px] text-[clamp(16px,1.4vw,19px)] leading-[1.7] text-mist-2">
            A collaboration with the Australian National University, tracking
            changes in Australia&apos;s forests and vegetation to inform carbon
            emissions and removals monitoring.
          </p>
          <p className="mt-0 mb-[22px] text-[17px] leading-[1.7] text-mist-3">
            An annually updated, AI-driven forest and vegetation map dating back
            to 2020. The model is trained on airborne LiDAR and runs over the
            full Sentinel-2 archive via Terrak.io — a living record of change
            rather than a snapshot, at a fraction of the cost of commissioning a
            new map each year.
          </p>

          <div className="grid grid-cols-1 gap-[clamp(20px,3vw,44px)] border-t border-white/[.14] pt-7 sm:grid-cols-3">
            {[
              { value: "10 m", label: "Pixel resolution" },
              { value: "2020", label: "Commencement of operation" },
              { value: "7.7M km²", label: "National coverage" },
            ].map((stat) => (
              <div key={stat.label} className="min-w-0">
                <div className="text-[clamp(30px,3.4vw,52px)] font-bold leading-[.95] tracking-[-.04em] text-paper">
                  {stat.value}
                </div>
                <div className="dc-mono mt-3.5 text-[12px] uppercase tracking-[.18em] text-mint">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <CaseLink
            href="/case-studies/vmap-vegetation-monitoring"
            label="Read the full case study"
          />
        </div>
      </div>

      {/* Nature Repair Market (PlanR) */}
      <div className="mt-[clamp(64px,8vw,120px)] flex flex-wrap-reverse gap-[clamp(28px,4vw,64px)] border-t border-white/[.14] pt-[clamp(48px,6vw,88px)]">
        <div className="min-w-0 flex-[1_1_440px]">
          <h3 className="mt-0 mb-5 text-[clamp(26px,3.4vw,52px)] font-semibold leading-[1.02] tracking-[-.032em] text-balance">
            Nature Repair Market (PlanR)
          </h3>
          <p className="mt-0 mb-[22px] text-[clamp(16px,1.4vw,19px)] leading-[1.7] text-mist-2">
            A national framework rewarding landholders and investors for
            measurable improvements to Australia&apos;s biodiversity.
          </p>
          <p className="mt-0 mb-[22px] text-[17px] leading-[1.7] text-mist-3">
            Terrak.io generates carbon and biodiversity assessments for any
            property in Australia — on demand, nationwide and at no cost to
            landholders. What would otherwise need a bespoke ecological
            assessment per property becomes a scalable operational capability.
          </p>

          <div className="grid grid-cols-1 gap-[clamp(20px,3vw,44px)] border-t border-white/[.14] pt-7 sm:grid-cols-3">
            {[
              { value: "100%", label: "National coverage" },
              { value: "Free", label: "Carbon and biodiversity assessments" },
              { value: "1,000+", label: "Monthly users" },
            ].map((stat) => (
              <div key={stat.label} className="min-w-0">
                <div className="text-[clamp(30px,3.4vw,52px)] font-bold leading-[.95] tracking-[-.04em] text-paper">
                  {stat.value}
                </div>
                <div className="dc-mono mt-3.5 text-[12px] uppercase tracking-[.18em] text-mint">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <CaseLink
            href="/case-studies/planr-platform"
            label="Read the full case study"
          />
        </div>

        <div className="group relative min-h-[clamp(260px,30vw,420px)] min-w-0 flex-[1_1_340px] overflow-hidden">
          <Image
            src="/images/aerial-view-of-australian-suburb.webp"
            alt="Property-scale carbon and biodiversity assessment"
            fill
            sizes="(max-width: 900px) 100vw, 40vw"
            className="object-cover saturate-[.85] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,15,28,.15),rgba(5,15,28,.75))]" />
          <div className="dc-mono absolute bottom-5 left-[22px] text-[12px] uppercase tracking-[.2em] text-mint">
            Property-scale carbon and biodiversity, nationwide
          </div>
        </div>
      </div>

      <div className="mt-[clamp(56px,7vw,96px)] border-t border-white/[.14] pt-[clamp(32px,4vw,52px)]">
        <Link
          href="/case-studies"
          className="inline-flex items-center gap-3 bg-mint px-8 py-4 text-base font-semibold text-ink transition-colors duration-300 hover:bg-paper"
        >
          See all our work <span className="dc-mono">→</span>
        </Link>
      </div>
    </div>
  </section>
);
